import { useEffect, useState } from "react";

import { sortBy } from "lodash";
import moment from "moment";

import { useUser } from "@components/user-context";
import { H1 } from "@components/utilities";
import { type Annotation } from "@features/custom-annotations";
import {
  CREATE_CUSTOM_ANNOTATION,
  DELETE_CUSTOM_ANNOTATION,
  GET_CUSTOM_ANNOTATIONS,
} from "@gql/custom-annotations";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type CreateCustomAnnotation,
  type CreateCustomAnnotationVariables,
  type DeleteCustomAnnotation,
  type DeleteCustomAnnotationVariables,
  type GetClientCustomCustomAnnotations,
} from "@north-beam/nb-client-side";
import { IconButton, PrimaryButton, SecondaryButton } from "@shared/buttons";
import { DatePicker } from "@shared/date-picker";
import { Modal } from "@shared/modal";
import { TextInput } from "@shared/text-input";
import { downloadGenericCsv } from "@utils/csv";

export function CustomAnnotations() {
  const { user } = useUser();
  const [description, setDescription] = useState<string>("");
  const [date, setDate] = useState<moment.Moment | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);

  const [isCreateAnnotationModalOpen, setIsCreateAnnotationModalOpen] =
    useState(false);

  const [addCustomAnnotation, { loading: isCreateCustomAnnotationLoading }] =
    useNorthbeamMutation<
      CreateCustomAnnotation,
      CreateCustomAnnotationVariables
    >(CREATE_CUSTOM_ANNOTATION);

  const [removeCustomAnnotation] = useNorthbeamMutation<
    DeleteCustomAnnotation,
    DeleteCustomAnnotationVariables
  >(DELETE_CUSTOM_ANNOTATION);

  const { loading, data } = useNorthbeamQuery<GetClientCustomCustomAnnotations>(
    GET_CUSTOM_ANNOTATIONS,
    {
      variables: {
        startDate: null,
        endDate: null,
      },
      fetchPolicy: "cache-and-network",
    },
  );

  const onCreateCustomAnnotation = async () => {
    if (!date) {
      return;
    }
    const annotatedDate = date.format("YYYY-MM-DD");
    const result = await addCustomAnnotation({
      variables: {
        description,
        annotatedDate,
      },
    });

    const newAnnotation = result.data?.attemptToAddCustomAnnotation;
    if (newAnnotation) {
      const annotationsCopy = [...annotations];
      const annotationIndex = annotationsCopy.findIndex(
        ({ id }) => id === newAnnotation.id,
      );

      if (annotationIndex !== -1) {
        annotationsCopy[annotationIndex] = newAnnotation;
        setAnnotations(annotationsCopy);
      } else {
        annotationsCopy.push(newAnnotation);
      }
      setAnnotations(sortBy(annotationsCopy, "annotatedDate").reverse());
    }

    setIsCreateAnnotationModalOpen(false);
  };

  const onDeleteCustomAnnotation = async (id: string) => {
    const an = annotations.filter((annotation) => {
      return annotation.id !== id;
    });
    setAnnotations(an);
    await removeCustomAnnotation({
      variables: {
        id,
      },
    });
  };

  useEffect(() => {
    setAnnotations(data?.me.clientCustomAnnotations || []);
  }, [data?.me?.clientCustomAnnotations]);

  return (
    <>
      <H1>Custom Annotations</H1>
      <hr />
      <Modal
        size="md"
        isOpen={isCreateAnnotationModalOpen}
        titleContent={
          <div className="font-bold text-xl">Create an annotation</div>
        }
        onClose={() => {
          setDescription("");
          setDate(null);
          setIsCreateAnnotationModalOpen(false);
        }}
      >
        <div className="space-y-4">
          <DatePicker
            label="Date"
            onChange={(value) => setDate(moment(value))}
            value={date?.toString()}
            timezone={user.timezone}
            appendToBody
          />
          <TextInput
            label="Description"
            className="pb-3"
            value={description}
            onChange={({ target }) => setDescription(target.value)}
          />
        </div>
        <div className="text-right mt-4">
          <PrimaryButton
            disabled={!date || !description || isCreateCustomAnnotationLoading}
            tabIndex={70}
            onClick={onCreateCustomAnnotation}
          >
            SUBMIT
          </PrimaryButton>
        </div>
      </Modal>
      <div className="mb-4 flex items-center justify-between">
        <h3>Annotation Archive</h3>
        <div className="flex items-center space-x-2">
          <SecondaryButton
            onClick={() =>
              downloadGenericCsv({
                fileName: "northbeam-custom-annotations.csv",
                headers: [
                  {
                    label: "Annotation Date",
                    key: "annotatedDate",
                  },
                  {
                    label: "Description",
                    key: "description",
                  },
                  {
                    label: "Created By",
                    key: "createdBy",
                  },
                  {
                    label: "Updated At",
                    key: "updatedAt",
                  },
                ],
                data: annotations.map((annotation) => ({
                  annotatedDate: annotation.annotatedDate,
                  description: annotation.description,
                  createdBy: annotation.user
                    ? `${annotation.user.first_name ?? ""} ${
                        annotation.user.last_name ?? ""
                      }`
                    : "",
                  updatedAt: moment(annotation.updatedAt).format("YYYY-MM-DD"),
                })),
              })
            }
          >
            EXPORT TO CSV
          </SecondaryButton>
          <PrimaryButton onClick={() => setIsCreateAnnotationModalOpen(true)}>
            CREATE ANNOTATION
          </PrimaryButton>
        </div>
      </div>
      {loading ? (
        <p>Please wait...</p>
      ) : (
        <>
          {annotations.map((annotation: Annotation) => (
            <div
              key={annotation.id}
              className="flex justify-between px-4 py-2 my-2 bg-white border rounded-md space-x-4"
            >
              <div>{annotation.annotatedDate}</div>
              <div className="flex-grow">{annotation.description}</div>
              <IconButton
                onClick={() => onDeleteCustomAnnotation(annotation.id)}
                variant="danger"
              >
                <i className="fa-regular fa-sharp fa-trash-alt" />
              </IconButton>
            </div>
          ))}
        </>
      )}
    </>
  );
}
